/**
 * DatePicker – calendar popover with month/year dropdown and consistent styling.
 * Use for DOB, signature date, landlord start/end dates. Value is YYYY-MM-DD.
 */
import React, { useRef, useState, useCallback, useEffect } from 'react';
import {
  format,
  parse,
  parseISO,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  addMonths,
  subMonths,
  isSameMonth,
  isSameDay,
  isToday,
  isValid,
  getYear,
  setMonth,
  setYear,
} from 'date-fns';
import { Calendar, ChevronDown } from 'lucide-react';

export interface DatePickerProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'value' | 'onChange'> {
  label?: string;
  error?: string;
  helperText?: string;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const YEAR_RANGE = 12;
const thisYear = getYear(new Date());

function parseValue(value: string | undefined): Date | null {
  if (!value || value.length < 10) return null;
  try {
    const d = parseISO(value.slice(0, 10));
    return isValid(d) ? d : null;
  } catch {
    return null;
  }
}

/** Parse user-typed date (mm/dd/yyyy or yyyy-mm-dd) to Date or null. */
function parseTypedDate(input: string): Date | null {
  const trimmed = input.trim();
  if (!trimmed) return null;
  try {
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      const d = parseISO(trimmed);
      return isValid(d) ? d : null;
    }
    const d = parse(trimmed, 'MM/dd/yyyy', new Date());
    return isValid(d) ? d : null;
  } catch {
    return null;
  }
}

export const DatePicker = React.forwardRef<HTMLInputElement, DatePickerProps>(function DatePicker(
  {
    label,
    error,
    helperText,
    className = '',
    id,
    value = '',
    onChange,
    placeholder = 'mm/dd/yyyy',
    name,
    ...props
  },
  _forwardRef
) {
  const propsWithRef = props as typeof props & { ref?: React.Ref<HTMLInputElement> };
  const { ref: inputRef, ...restProps } = propsWithRef;
  const fieldRef = inputRef ?? _forwardRef;
  const generatedId = React.useId();
  const fieldId = id || `date-${generatedId.replaceAll(':', '-')}`;
  const containerRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<'calendar' | 'monthyear'>('calendar');
  const [viewDate, setViewDate] = useState<Date>(() => parseValue(value) || new Date());
  const parsed = parseValue(value);
  const displayValue = parsed ? format(parsed, 'MM/dd/yyyy') : '';
  const [inputText, setInputText] = useState(displayValue);

  useEffect(() => {
    setInputText(displayValue);
  }, [displayValue]);

  const viewDateFromValue = parseValue(value);
  const displayViewDate = viewDateFromValue ?? viewDate;

  const handleInputBlur = useCallback(() => {
    const d = parseTypedDate(inputText);
    if (d) {
      const iso = format(d, 'yyyy-MM-dd');
      onChange?.({ target: { name: name ?? '', value: iso } } as React.ChangeEvent<HTMLInputElement>);
      setInputText(format(d, 'MM/dd/yyyy'));
    } else if (inputText.trim() === '') {
      onChange?.({ target: { name: name ?? '', value: '' } } as React.ChangeEvent<HTMLInputElement>);
    } else {
      setInputText(displayValue);
    }
  }, [inputText, onChange, name, displayValue]);

  const close = useCallback(() => {
    setOpen(false);
    setView('calendar');
  }, []);

  useEffect(() => {
    if (!open) return;
    const handle = (e: MouseEvent) => {
      if (containerRef.current?.contains(e.target as Node)) return;
      close();
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open, close]);

  const selectDate = useCallback(
    (d: Date) => {
      const iso = format(d, 'yyyy-MM-dd');
      onChange?.({ target: { name: name ?? '', value: iso } } as React.ChangeEvent<HTMLInputElement>);
      close();
    },
    [onChange, close, name]
  );

  const goToMonthYear = useCallback((monthIndex: number, year: number) => {
    setViewDate(setMonth(setYear(new Date(), year), monthIndex));
    setView('calendar');
  }, []);

  const inputClasses = `
    w-full border rounded-lg text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-white
    focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-colors
    ${error ? 'border-red-500 focus:ring-red-500' : 'border-slate-300 dark:border-slate-600'}
    px-3 py-2.5
    ${className}
  `;

  const monthStart = startOfMonth(displayViewDate);
  const monthEnd = endOfMonth(displayViewDate);
  const start = startOfWeek(monthStart);
  const end = endOfWeek(monthEnd);
  const rows: Date[] = [];
  let d = start;
  while (d <= end) {
    rows.push(d);
    d = addDays(d, 1);
  }

  const years = Array.from({ length: YEAR_RANGE * 2 + 1 }, (_, i) => thisYear - YEAR_RANGE + i);

  return (
    <div className="w-full relative" ref={containerRef}>
      {label && (
        <label
          htmlFor={fieldId}
          className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1"
        >
          {label}
        </label>
      )}
      <input
        type="hidden"
        ref={fieldRef}
        id={fieldId}
        name={name}
        value={value}
        onChange={onChange}
        aria-hidden
        {...restProps}
      />
      <div className={`${inputClasses} flex items-center gap-2`}>
        <input
          type="text"
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          onBlur={handleInputBlur}
          placeholder={placeholder}
          disabled={restProps.disabled}
          aria-label={label ? `Date for ${label}` : 'Date'}
          aria-haspopup="dialog"
          aria-expanded={open}
          className="flex-1 min-w-0 bg-transparent border-0 p-0 text-inherit placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-0"
        />
        <button
          type="button"
          tabIndex={0}
          aria-label={label ? `Open calendar for ${label}` : 'Open calendar'}
          aria-haspopup="dialog"
          aria-expanded={open}
          onClick={(e) => {
            e.preventDefault();
            setOpen((o) => !o);
          }}
          className="shrink-0 p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
        >
          <Calendar size={18} aria-hidden />
        </button>
      </div>

      {open && (
        <div
          className="absolute z-50 mt-1 p-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg shadow-lg min-w-[280px]"
          role="dialog"
          aria-label="Calendar"
        >
          {view === 'calendar' ? (
            <>
              <button
                type="button"
                className="w-full flex items-center justify-between gap-2 py-1.5 px-2 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-sm font-medium text-slate-700 dark:text-slate-200"
                onClick={() => setView('monthyear')}
                aria-expanded="false"
                aria-haspopup="listbox"
              >
                {format(displayViewDate, 'MMMM yyyy')}
                <ChevronDown size={16} className="text-slate-500" aria-hidden />
              </button>
              <div className="grid grid-cols-7 gap-0.5 mt-2 text-center text-xs text-slate-500 dark:text-slate-400 mb-1">
                {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day) => (
                  <span key={day}>{day}</span>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-0.5">
                {rows.map((day) => {
                  const inMonth = isSameMonth(day, displayViewDate);
                  const selected = parsed && isSameDay(day, parsed);
                  const today = isToday(day);
                  return (
                    <button
                      key={day.getTime()}
                      type="button"
                      className={`
                        w-8 h-8 rounded text-sm
                        ${inMonth ? 'text-slate-700 dark:text-slate-200' : 'text-slate-300 dark:text-slate-600'}
                        ${selected ? 'bg-brand-600 text-white' : today ? 'ring-1 ring-brand-500' : ''}
                        ${inMonth && !selected ? 'hover:bg-slate-100 dark:hover:bg-slate-700' : ''}
                      `}
                      onClick={() => inMonth && selectDate(day)}
                      aria-label={format(day, 'MMMM d, yyyy')}
                      aria-selected={selected || undefined}
                    >
                      {format(day, 'd')}
                    </button>
                  );
                })}
              </div>
              <div className="flex justify-between mt-2 pt-2 border-t border-slate-200 dark:border-slate-600">
                <button
                  type="button"
                  className="text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                  onClick={() => setViewDate(subMonths(displayViewDate, 1))}
                  aria-label="Previous month"
                >
                  ← Prev
                </button>
                <button
                  type="button"
                  className="text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                  onClick={() => setViewDate(addMonths(displayViewDate, 1))}
                  aria-label="Next month"
                >
                  Next →
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="text-sm font-medium text-slate-700 dark:text-slate-200 mb-2">
                Select month and year
              </div>
              <div className="flex gap-3">
                <div
                  className="flex-1 overflow-y-auto max-h-48 border border-slate-200 dark:border-slate-600 rounded p-1"
                  role="listbox"
                  aria-label="Year"
                >
                  {years.map((y) => (
                    <button
                      key={y}
                      type="button"
                      role="option"
                      aria-selected={getYear(displayViewDate) === y}
                      className={`block w-full text-left py-1 px-2 rounded text-sm ${
                        getYear(displayViewDate) === y
                          ? 'bg-brand-600 text-white'
                          : 'hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200'
                      }`}
                      onClick={() => goToMonthYear(new Date(displayViewDate).getMonth(), y)}
                    >
                      {y}
                    </button>
                  ))}
                </div>
                <div className="grid grid-cols-3 gap-1" role="listbox" aria-label="Month">
                  {MONTHS.map((monthName, i) => (
                    <button
                      key={monthName}
                      type="button"
                      role="option"
                      aria-selected={displayViewDate.getMonth() === i}
                      className={`py-1.5 px-2 rounded text-sm ${
                        displayViewDate.getMonth() === i
                          ? 'bg-brand-600 text-white'
                          : 'hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200'
                      }`}
                      onClick={() => goToMonthYear(i, getYear(displayViewDate))}
                    >
                      {monthName}
                    </button>
                  ))}
                </div>
              </div>
              <button
                type="button"
                className="mt-2 text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                onClick={() => setView('calendar')}
              >
                ← Back to calendar
              </button>
            </>
          )}
        </div>
      )}

      {error && (
        <p id={`${fieldId}-error`} className="mt-1 text-xs text-red-600 dark:text-red-400" role="alert">
          {error}
        </p>
      )}
      {helperText && !error && (
        <p id={`${fieldId}-help`} className="mt-1 text-xs text-slate-500 dark:text-slate-400">
          {helperText}
        </p>
      )}
    </div>
  );
});
